import { useCallback, useEffect, useState } from 'react';

/**
 * useCookieConsent — Tiny GDPR/PECR-compliant consent store, scoped
 * to a single organisation slug.
 *
 * Design notes:
 *  • Categories: `essential` is always-on (session, CSRF), `analytics`
 *    is optional (GA4, FB Pixel, custom tags). Plausible is cookie-less
 *    so we still gate it under `analytics` for a consistent UX — an
 *    admin can choose to move it if they wish, but "no consent = no
 *    tracking" is the safest default.
 *  • Storage is per-slug so each nursery's visitors get an
 *    independent choice — visiting `nurseryA` then `nurseryB` doesn't
 *    silently opt you into the second.
 *  • We store both the choice AND a version. Bumping `CONSENT_VERSION`
 *    invalidates every prior choice and forces the banner to reappear
 *    — use this when we materially change what we track.
 *  • `hasChoice === false` means "banner should show". `true` means
 *    "banner is dismissed" — regardless of accept vs reject.
 *  • All writes cross-tab broadcast via `storage` events so opening a
 *    second tab and dismissing there instantly hides the banner here.
 */

const CONSENT_VERSION = 1;
const keyFor = (slug) => `kw_consent_v${CONSENT_VERSION}_${slug || 'default'}`;

const DEFAULT_STATE = {
  hasChoice: false,
  choice: null,        // 'accepted' | 'rejected' | null
  categories: {
    essential: true,   // always true — required for the site to work
    analytics: false,  // opt-in
  },
  decidedAt: null,
};

function readStorage(slug) {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(keyFor(slug));
    if (!raw) return DEFAULT_STATE;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_STATE;
    return {
      hasChoice: !!parsed.hasChoice,
      choice: parsed.choice || null,
      categories: {
        essential: true,
        analytics: !!(parsed.categories && parsed.categories.analytics),
      },
      decidedAt: parsed.decidedAt || null,
    };
  } catch (_e) {
    return DEFAULT_STATE;
  }
}

function writeStorage(slug, state) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(keyFor(slug), JSON.stringify(state));
  } catch (_e) { /* private mode / quota — ignore, banner will re-appear next visit */ }
}

export function useCookieConsent(slug) {
  const [state, setState] = useState(() => readStorage(slug));

  // Re-read when the slug changes (visiting a different nursery).
  useEffect(() => { setState(readStorage(slug)); }, [slug]);

  // Cross-tab sync — react to writes from another tab.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handler = (e) => {
      if (e.key === keyFor(slug)) setState(readStorage(slug));
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [slug]);

  const commit = useCallback((next) => {
    const payload = {
      ...next,
      hasChoice: true,
      decidedAt: new Date().toISOString(),
    };
    writeStorage(slug, payload);
    setState(payload);
  }, [slug]);

  const acceptAll = useCallback(() => commit({
    choice: 'accepted',
    categories: { essential: true, analytics: true },
  }), [commit]);

  const rejectAll = useCallback(() => commit({
    choice: 'rejected',
    categories: { essential: true, analytics: false },
  }), [commit]);

  const setCategory = useCallback((name, value) => {
    if (name === 'essential') return; // cannot toggle essential
    commit({
      choice: value ? 'accepted' : 'rejected',
      categories: { ...state.categories, [name]: !!value },
    });
  }, [commit, state.categories]);

  const revoke = useCallback(() => {
    if (typeof window === 'undefined') return;
    try { window.localStorage.removeItem(keyFor(slug)); } catch (_e) { /* ignore */ }
    setState(DEFAULT_STATE);
  }, [slug]);

  return {
    ...state,
    // Convenience booleans for common gates
    analyticsAllowed: !!state.categories.analytics,
    acceptAll, rejectAll, setCategory, revoke,
  };
}
